<template>
  <a-drawer
    :width="720"
    :open="open"
    class="manager-notification-view-drawer"
    :body-style="{ padding: '32px' }"
    :footer="null"
    destroy-on-close
    @close="handleClose"
  >
    <template #title>
      <span class="manager-notification-view-drawer__title">Уведомление</span>
    </template>

    <template #closeIcon>
      <span class="manager-notification-view-drawer__close-trigger">
        <CloseOutlined />
      </span>
    </template>

    <div v-if="loading" class="manager-notification-view-drawer__loading">Загрузка...</div>

    <template v-else>
      <section class="manager-notification-view-drawer__meta">
        <div class="field-with-label">
          <span class="field-with-label__label">Тема</span>
          <span class="field-with-label__value">{{ detail.subject || '—' }}</span>
        </div>

        <div class="field-with-label">
          <span class="field-with-label__label">Дата / время</span>
          <span class="field-with-label__value">{{ detail.date || '—' }}</span>
        </div>

        <div class="field-with-label">
          <span class="field-with-label__label">Статус</span>
          <span
            class="manager-notification-view-drawer__status"
            :class="`manager-notification-view-drawer__status--${detail.statusModifier}`"
          >
            <span class="manager-notification-view-drawer__status-dot" aria-hidden="true"></span>
            {{ detail.status }}
          </span>
        </div>
      </section>

      <section class="manager-notification-view-drawer__message">
        <div class="field-with-label">
          <span class="field-with-label__label">Уведомление</span>
          <div class="manager-notification-view-drawer__message-text">
            <ContentTagsHtml v-if="detail.message" :html="detail.message" />
            <p v-else>—</p>
          </div>
        </div>
      </section>

      <section v-if="detail.files.length" class="manager-notification-view-drawer__files">
        <span class="field-with-label__label">Прикреплённые файлы</span>
        <ul class="manager-notification-view-drawer__file-list">
          <li
            v-for="(file, index) in detail.files"
            :key="`${file.url}-${file.name}-${index}`"
            class="manager-notification-view-drawer__file-item"
          >
            <img
              :src="getDocFormatIconPath(file.name)"
              alt=""
              class="manager-notification-view-drawer__file-icon"
            />
            <div class="manager-notification-view-drawer__file-content">
              <a :href="file.url" target="_blank" rel="noopener noreferrer" download>
                {{ file.name }}
              </a>
              <span v-if="formatFileMeta(file)">{{ formatFileMeta(file) }}</span>
            </div>
          </li>
        </ul>
      </section>

      <section v-if="detail.residentReplies.length" class="manager-notification-view-drawer__reply">
        <span class="field-with-label__label">Ответ резидента</span>

        <div class="manager-notification-view-drawer__reply-list">
          <article
            v-for="reply in detail.residentReplies"
            :key="reply.id"
            class="manager-notification-view-drawer__reply-item"
          >
            <div class="field-with-label">
              <span class="field-with-label__label">Дата</span>
              <span class="field-with-label__value">{{ reply.date || '—' }}</span>
            </div>

            <div class="field-with-label">
              <span class="field-with-label__label">Ответ</span>
              <span class="field-with-label__value manager-notification-view-drawer__reply-text">
                {{ reply.text || '—' }}
              </span>
            </div>
          </article>
        </div>
      </section>
    </template>
  </a-drawer>
</template>

<script setup lang="ts">
import { reactive, ref, watch } from 'vue';
import { CloseOutlined } from '@ant-design/icons-vue';
import ContentTagsHtml from '@/js/components/content-tags/content-tags-html.vue';
import { getDocFormatIconPath, getFileFormatLabel } from '@/js/utils/file-upload-utils';
import {
  getManagerNotificationStatusLabel,
  getManagerNotificationStatusModifier,
  normalizeNotificationFiles,
  normalizeNotificationStatusCode,
} from '../resident-notifications/map-notification';
import type { NotificationFile } from '../resident-notifications/types';
import type { ManagerNotificationListItem } from './manager-notification-api';
import { isManagerNotificationsMockEnabled } from './is-manager-notifications-mock';

type NotificationReplyItem = {
  id?: number;
  createdAt?: string;
  text?: string;
  managerReply?: boolean;
  files?: unknown[];
};

type ResidentReplyViewItem = {
  id: number;
  date: string;
  text: string;
};

const props = defineProps<{
  open: boolean;
  message: ManagerNotificationListItem | null;
}>();

const emit = defineEmits<{
  (e: 'update:open', value: boolean): void;
  (e: 'marked-read'): void;
}>();

const loading = ref(false);

const detail = reactive({
  subject: '',
  date: '',
  status: '',
  statusModifier: 'read',
  message: '',
  residentReplies: [] as ResidentReplyViewItem[],
  files: [] as NotificationFile[],
});

watch(
  () => [props.open, props.message] as const,
  ([isOpen, message]) => {
    if (!isOpen || !message) {
      return;
    }

    void loadNotification(message);
  },
);

function formatDrawerDate(iso: string): string {
  if (!iso) return '';

  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;

  const datePart = date.toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
  const timePart = date.toLocaleTimeString('ru-RU', {
    hour: '2-digit',
    minute: '2-digit',
  });

  return `${datePart} / ${timePart}`;
}

function handleClose(): void {
  emit('update:open', false);
}

function formatFileMeta(file: NotificationFile): string {
  const type = file.type?.trim() || getFileFormatLabel(file.name);
  const size = file.size?.trim();

  return [type, size].filter(Boolean).join(' / ');
}

function mapResidentReplies(replies: unknown): ResidentReplyViewItem[] {
  if (!Array.isArray(replies) || replies.length === 0) {
    return [];
  }

  return replies
    .filter((reply): reply is NotificationReplyItem => {
      return (
        Boolean(reply) &&
        typeof reply === 'object' &&
        (reply as NotificationReplyItem).managerReply === false
      );
    })
    .sort((left, right) => {
      const leftTime = left.createdAt ? new Date(left.createdAt).getTime() : 0;
      const rightTime = right.createdAt ? new Date(right.createdAt).getTime() : 0;
      return leftTime - rightTime;
    })
    .map((reply, index) => ({
      id: reply.id ?? index,
      date: formatDrawerDate(reply.createdAt ?? ''),
      text: reply.text?.trim() ?? '',
    }))
    .filter((reply) => reply.date || reply.text);
}

function applyNotificationStatus(status?: string | null): void {
  const statusCode = normalizeNotificationStatusCode(status);
  detail.statusModifier = getManagerNotificationStatusModifier(statusCode);
  detail.status = getManagerNotificationStatusLabel(statusCode);
}

function applyListMessage(message: ManagerNotificationListItem): void {
  detail.subject = message.subject ?? '';
  detail.date = formatDrawerDate(message.createdAt);
  applyNotificationStatus(message.status);
  detail.message = message.text ?? '';
  detail.residentReplies = mapResidentReplies(message.replies);
  detail.files = normalizeNotificationFiles(message.files);
}

async function loadNotification(message: ManagerNotificationListItem): Promise<void> {
  applyListMessage(message);

  if (isManagerNotificationsMockEnabled()) {
    loading.value = false;
    return;
  }

  loading.value = true;

  try {
    const { fetchManagerNotificationDetail, markManagerNotificationRead } =
      await import('./manager-notification-api');

    const [data] = await Promise.all([
      fetchManagerNotificationDetail(message.id),
      markManagerNotificationRead(message.id),
    ]);

    detail.subject = data.subject ?? detail.subject;
    detail.date = data.createdAt ? formatDrawerDate(data.createdAt) : detail.date;
    applyNotificationStatus(data.status ?? message.status);
    detail.message = data.message ?? detail.message;
    detail.files = normalizeNotificationFiles(data.files);

    emit('marked-read');
  } catch (error) {
    console.error('Ошибка загрузки уведомления:', error);
  } finally {
    loading.value = false;
  }
}
</script>
